// What a trader owes, in SAR — read from the payment_quote() database function,
// never computed here.
//
// payment_quote() (supabase/migrations/20260929091332_fx_rate_lock.sql) is THE calculation: the
// figure on the trader's card and Pay button, the figure telr-create sends to
// Telr, and the figure telr-verify requires back, to the halala. It converts at
// the rate LOCKED on the price source — on the offer when it was accepted, on
// the invoice when it was issued (USD: the admin-set rate, 3.79 by default;
// SAR: 1; CNY: not payable online yet) — then adds the visible 5% fee and
// splits the 30% deposit. Before acceptance, offer_amounts_sar() prices the
// trader's offer cards with the same arithmetic at today's rate.
//
// This file replaces src/lib/offerCharge.js (2026-09-29), which restated the
// server's arithmetic in the browser and needed a parity test to stay honest.
// Nothing here multiplies by a rate. If you are about to, call the RPC instead.
//
// Quote shape (trader): { payable, reason, source, request_id, invoice_id,
//   invoice_status, goods_sar, fee_pct, fee_sar, total_sar, deposit_sar,
//   first_paid_sar, balance_sar, lines_sar }  — SAR only. `currency`, `rate`,
//   `rate_locked_at` and `goods` (the supplier's own figure) are filled for
//   admins and the service role only: the rate is internal (owner, 2026-09-29).
// `payable: false` carries a `reason` (no_price_source · ambiguous_offer ·
// invalid_amount · currency_not_payable · rate_not_locked) and no amounts.
// Mirrored in maabar-app — see docs/mobile-mirror-backlog.md §5.

// null when the call fails — the caller shows no Pay button rather than a guess.
export async function fetchPaymentQuote(sb, { requestId = null, invoiceId = null } = {}) {
  if (!requestId && !invoiceId) return null;
  const { data, error } = await sb.rpc('payment_quote', { p_request_id: requestId, p_invoice_id: invoiceId });
  if (error || !data) {
    console.error('[paymentQuote] payment_quote failed for', requestId || invoiceId, ':', error?.code || '', error?.message || 'no data');
    return null;
  }
  return data;
}

// Offers in SAR for the trader's offer cards, pending OR accepted — from
// offer_amounts_sar(), the arithmetic payment_quote() charges from (pending at
// today's rate, accepted at the locked one). One call for the whole list.
// Returns { [offerId]: { payable, reason, rate_locked, unit_price_sar,
// shipping_sar, sample_price_sar, goods_sar, fee_sar, total_sar } }; an offer
// missing from the map (or a failed call → {}) shows no amount, never a guess.
export async function fetchOfferAmounts(sb, offerIds) {
  const ids = [...new Set((offerIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await sb.rpc('offer_amounts_sar', { p_offer_ids: ids });
  if (error) {
    console.error('[paymentQuote] offer_amounts_sar failed:', error.code || '', error.message || error);
    return {};
  }
  return (data || []).reduce((acc, row) => { acc[row.offer_id] = row; return acc; }, {});
}

// The SAR amount Telr charges for one stage: 'deposit' | 'full' | 'balance'.
// null when there is no such charge (not payable, no deposit yet, nothing owed).
// CHANGED 2026-09-29: the first payment is reduced by the request fee credit —
// payment_quote() returns deposit_due_sar / full_due_sar for exactly that (and
// deposit_due_sar null when the credit covers the whole deposit). A quote from
// before that migration has neither key and falls back to the gross figures.
export function stageAmountSar(quote, stage) {
  if (!quote?.payable) return null;
  const has = (k) => Object.prototype.hasOwnProperty.call(quote, k);
  const pick = stage === 'deposit' ? (has('deposit_due_sar') ? quote.deposit_due_sar : quote.deposit_sar)
    : stage === 'full' ? (has('full_due_sar') ? quote.full_due_sar : quote.total_sar)
    : stage === 'balance' ? quote.balance_sar
    : null;
  const n = Number(pick);
  return pick != null && Number.isFinite(n) && n > 0 ? n : null;
}

// The request fee credit the NEXT payment carries, or 0. Shown as its own line
// in the payment summary; never folded into the goods or the 5%.
export function feeCreditSar(quote) {
  const n = Number(quote?.fee_credit_sar);
  return quote?.payable && Number.isFinite(n) && n > 0 ? n : 0;
}

// "3,937.50" — always two decimals, because Telr shows two.
export function formatSar(n) {
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// The note under every SAR total a trader sees before paying (owner,
// 2026-09-29). The exchange rate is INTERNAL: traders see SAR only — never
// the supplier's USD/CNY figure, never a rate — so this line says the price
// is in SAR and when it was fixed, and nothing about conversion.
// `source` is 'offer' (fixed on acceptance) or 'invoice' (fixed on issue).
// Traders read Arabic or English; Chinese is the supplier's language, so a
// 'zh' reader gets the English line.
export function sarFixedNote(source, lang) {
  const invoice = source === 'invoice';
  if (lang === 'ar') {
    return invoice
      ? 'السعر بالريال، ومثبّت من لحظة إصدار الفاتورة'
      : 'السعر بالريال، ومثبّت من لحظة قبولك العرض';
  }
  return invoice
    ? 'Price in SAR, fixed from the moment the invoice is issued'
    : 'Price in SAR, fixed from the moment you accept the offer';
}
